import styled from "styled-components";
import { GradientActionButton, PrimaryAction } from "./shell";
import type { ThemeMode, TimezoneTone } from "../types";

type ThemeModeProps = {
    themeMode?: ThemeMode;
};

type ActiveProps = {
    active?: boolean;
};

type OpenProps = {
    open?: boolean;
};

type ToneProps = {
    background?: string;
    color?: string;
};

export const SuggestionCard = styled.div<{ dismissed?: boolean }>`
    border: 1px solid ${p => p.dismissed ? "var(--calendar-border)" : "rgba(66, 231, 182, 0.26)"};
    background: ${p => p.dismissed ? "var(--calendar-surface)" : "linear-gradient(135deg, rgba(66, 231, 182, 0.08), rgba(109, 116, 255, 0.1))"};
    border-radius: var(--calendar-radius-card);
    padding: 0.9rem;
    display: grid;
    gap: 0.7rem;
`;

export const SuggestionActions = styled.div`
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
`;

export const LocationFieldWrap = styled.div<OpenProps>`
    position: relative;
    z-index: ${p => p.open ? 30 : "auto"};
`;

export const LocationSuggestionList = styled.div<ThemeModeProps>`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 31;
    border: 1px solid var(--calendar-border-strong);
    background: ${p => p.themeMode === "dark"
        ? "linear-gradient(180deg, rgba(16, 19, 29, 0.98), rgba(22, 26, 36, 0.98))"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(245, 247, 251, 0.99))"};
    border-radius: var(--calendar-radius-card);
    box-shadow: 0 22px 44px rgba(15, 23, 42, 0.18);
    padding: 0.35rem;
    display: grid;
    gap: 0.2rem;
    max-height: 240px;
    overflow-y: auto;
    backdrop-filter: blur(12px);
`;

export const LocationSuggestionButton = styled.button<ThemeModeProps>`
    border: 1px solid transparent;
    background: ${p => p.themeMode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.7)"};
    color: var(--calendar-text);
    text-align: left;
    padding: 0.65rem 0.75rem;
    border-radius: var(--calendar-radius-control);
    display: grid;
    gap: 0.15rem;
    cursor: pointer;

    &:hover {
        background: var(--calendar-surface-secondary);
        border-color: var(--calendar-border);
    }
`;

export const SuggestionRestoreRow = styled.div`
    display: flex;
    justify-content: flex-start;
`;

export const FieldHint = styled.div`
    margin-top: 0.45rem;
    font-size: 11px;
    color: var(--calendar-text-soft);
`;

export const TimezoneCalculatorCard = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    padding: 0.85rem;
    display: grid;
    gap: 0.7rem;
`;

export const TimezoneAttendeeList = styled.div`
    display: grid;
    gap: 0.5rem;
`;

export const TimezoneAttendeeRow = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 0.55rem;
    align-items: center;
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface);
    border-radius: var(--calendar-radius-card);
    padding: 0.7rem 0.75rem;
`;

export const TimezoneFlagBadge = styled.span`
    width: 30px;
    height: 30px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    flex: 0 0 auto;
`;

export const TimezoneRemoveButton = styled.button`
    width: 26px;
    height: 26px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface);
    color: var(--calendar-text-soft);
    cursor: pointer;
    font-size: 13px;
    line-height: 1;
`;

export const TimezonePickerWrap = styled.div<OpenProps>`
    position: relative;
    z-index: ${p => p.open ? 35 : "auto"};
`;

export const TimezonePickerButton = styled(GradientActionButton)`
    width: 100%;
    height: auto;
    min-height: 44px;
    padding: 0.65rem 0.85rem;
    text-align: center;
`;

export const TimezonePickerPanel = styled.div<ThemeModeProps>`
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 36;
    border: 1px solid var(--calendar-border-strong);
    background: ${p => p.themeMode === "dark"
        ? "linear-gradient(180deg, rgba(16, 19, 29, 0.98), rgba(22, 26, 36, 0.98))"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(245, 247, 251, 0.99))"};
    border-radius: var(--calendar-radius-card);
    box-shadow: 0 22px 44px rgba(15, 23, 42, 0.18);
    padding: 0.45rem;
    display: grid;
    gap: 0.35rem;
    max-height: min(420px, 70vh);
    overflow: visible;
    backdrop-filter: blur(12px);
`;

export const TimezonePickerResults = styled.div`
    max-height: 260px;
    overflow-y: scroll;
    overscroll-behavior: contain;
    display: grid;
    gap: 0.25rem;
    padding-right: 0.1rem;
`;

export const TimezonePickerGroupHeader = styled.div`
    color: var(--calendar-text-soft);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 0.55rem 0.75rem 0.25rem;
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--calendar-shell);
`;

export const TimezoneOptionButton = styled.button<ThemeModeProps>`
    border: 1px solid transparent;
    background: ${p => p.themeMode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.7)"};
    color: var(--calendar-text);
    text-align: left;
    padding: 0.65rem 0.75rem;
    border-radius: var(--calendar-radius-control);
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.6rem;
    align-items: center;
    cursor: pointer;

    &:hover {
        background: var(--calendar-surface-secondary);
        border-color: var(--calendar-border);
    }
`;

export const TimezoneTimeline = styled.div`
    display: grid;
    grid-template-columns: repeat(24, minmax(18px, 1fr));
    align-items: end;
    gap: 0.24rem;
`;

export const TimezoneMatchList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.5rem;
`;

export const TimezoneMatchItem = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    padding: 0.65rem 0.75rem;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.65rem;
    align-items: center;
`;

export const TimezoneMatchRole = styled.div`
    color: var(--calendar-text-soft);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

export const TimezoneHourButton = styled.button<ActiveProps>`
    border: 1px solid ${p => p.active ? "var(--calendar-accent)" : "var(--calendar-border)"};
    background: var(--calendar-surface);
    border-radius: var(--calendar-radius-control);
    min-width: 0;
    height: 92px;
    padding: 0.25rem 0.15rem;
    display: grid;
    align-content: end;
    gap: 0.28rem;
    color: var(--calendar-text-soft);
    cursor: pointer;
    box-shadow: ${p => p.active ? "0 0 0 4px var(--calendar-accent-soft)" : "none"};
`;

export const TimezoneHourBar = styled.div<{ height: number; score: number; tone: TimezoneTone }>`
    width: 100%;
    height: ${p => `${p.height}px`};
    min-height: ${p => p.score > 0 ? "12px" : "6px"};
    border-radius: var(--calendar-radius-task);
    background: ${p => p.tone === "prime"
        ? "linear-gradient(180deg, #42e7b6, #22c55e)"
        : p.tone === "decent"
            ? "linear-gradient(180deg, #f8c860, #f59e0b)"
            : "var(--calendar-surface-tertiary)"};
`;

export const TimezoneSuggestionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.55rem;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const SuggestionsFeedSection = styled.div`
    display: grid;
    gap: 0.75rem;
`;

export const SuggestionsFeedHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

export const SuggestionsFeedCards = styled.div`
    display: grid;
    gap: 0.65rem;
`;

export const SuggestionFeedCard = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    padding: 0.85rem;
    display: grid;
    gap: 0.7rem;
`;

export const SuggestionFeedCardHeader = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 0.7rem;
    align-items: start;
`;

export const SuggestionIndicator = styled.div<ToneProps>`
    width: 32px;
    height: 32px;
    border-radius: var(--calendar-radius-control);
    background: ${p => p.background || "var(--calendar-accent-soft)"};
    color: ${p => p.color || "var(--calendar-accent)"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
`;

export const SuggestionFeedBody = styled.div`
    display: grid;
    gap: 0.28rem;
`;

export const SuggestionFeedTitle = styled.div`
    font-size: 13px;
    line-height: 1.45;
    font-weight: 700;
`;

export const SuggestionFeedMeta = styled.div`
    font-size: 11px;
    color: var(--calendar-text-soft);
`;

export const SuggestionDismissButton = styled.button`
    width: 26px;
    height: 26px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface);
    color: var(--calendar-text-soft);
    cursor: pointer;
    font-size: 13px;
    line-height: 1;
`;

export const SuggestionFeedActions = styled.div`
    display: flex;
    gap: 0.55rem;
    flex-wrap: wrap;
`;

export const SuggestionActionButton = styled(PrimaryAction)`
    height: auto;
    min-height: 34px;
    border-radius: var(--calendar-radius-badge);
    padding: 0.45rem 0.8rem;
    font-size: 11px;
    font-weight: 800;
`;

export const SuggestionsRefreshButton = styled.button`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    min-height: 32px;
    border-radius: var(--calendar-radius-badge);
    padding: 0.35rem 0.7rem;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
`;
